/**
 * Centralized storage access for evaluation session data.
 * All sessionStorage/localStorage interactions go through this module.
 *
 * Why: Components were directly accessing sessionStorage with duplicated
 * key-building logic across 5+ files. This makes the code untestable and
 * error-prone (typos in key names, inconsistent fallback logic).
 *
 * How to extend: Add new get/set/clear functions here. Inject `storage`
 * parameter for unit testing.
 */

// ─── Types ───────────────────────────────────────────────────────────

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  keys(): string[];
}

// ─── Default adapters (browser) ──────────────────────────────────────

const sessionAdapter: StorageAdapter = {
  getItem: (k) => sessionStorage.getItem(k),
  setItem: (k, v) => sessionStorage.setItem(k, v),
  removeItem: (k) => sessionStorage.removeItem(k),
  keys: () => Object.keys(sessionStorage),
};

const localAdapter: StorageAdapter = {
  getItem: (k) => localStorage.getItem(k),
  setItem: (k, v) => localStorage.setItem(k, v),
  removeItem: (k) => localStorage.removeItem(k),
  keys: () => Object.keys(localStorage),
};

// ─── Key builders (single source of truth) ───────────────────────────

const EVAL_ID_PREFIX = "evaluationId_";
const EVALUATED_IDS_PREFIX = "evaluatedIds_";
const HANDOUT_DATA_KEY = "__handoutData";
const HANDOUT_PRINT_KEY = "handoutDataForPrint";

// ─── Evaluation ID ───────────────────────────────────────────────────

export function getEvaluationId(
  employeeId: string | null,
  storage: StorageAdapter = sessionAdapter,
): string | null {
  if (employeeId) {
    const value = storage.getItem(`${EVAL_ID_PREFIX}${employeeId}`);
    if (value) return value;
  }
  // Fallback: find any evaluationId key
  try {
    const evalKey = storage.keys().find((k) => k.startsWith(EVAL_ID_PREFIX));
    return evalKey ? storage.getItem(evalKey) : null;
  } catch {
    return null;
  }
}

export function setEvaluationId(
  employeeId: string,
  evaluationId: string,
  storage: StorageAdapter = sessionAdapter,
): void {
  storage.setItem(`${EVAL_ID_PREFIX}${employeeId}`, evaluationId);
}

// ─── Evaluated Competency IDs ────────────────────────────────────────

export function getEvaluatedIds(
  employeeId: string,
  storage: StorageAdapter = sessionAdapter,
): string[] | null {
  try {
    const raw = storage.getItem(`${EVALUATED_IDS_PREFIX}${employeeId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setEvaluatedIds(
  employeeId: string,
  ids: string[],
  storage: StorageAdapter = sessionAdapter,
): void {
  storage.setItem(`${EVALUATED_IDS_PREFIX}${employeeId}`, JSON.stringify(ids));
}

// ─── Employee ID recovery (from any evaluationId key) ────────────────

export function recoverEmployeeIdFromStorage(
  storage: StorageAdapter = sessionAdapter,
): string | null {
  try {
    const evalKey = storage.keys().find((k) => k.startsWith(EVAL_ID_PREFIX));
    return evalKey ? evalKey.replace(EVAL_ID_PREFIX, "") : null;
  } catch {
    return null;
  }
}

// ─── Handout data (localStorage) ─────────────────────────────────────

export function getHandoutData(
  storage: StorageAdapter = localAdapter,
): unknown | null {
  try {
    const raw = storage.getItem(HANDOUT_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setHandoutData(
  data: unknown,
  storage: StorageAdapter = localAdapter,
): void {
  storage.setItem(HANDOUT_DATA_KEY, JSON.stringify(data));
}

export function getHandoutPrintData(
  storage: StorageAdapter = localAdapter,
): unknown | null {
  try {
    const raw = storage.getItem(HANDOUT_PRINT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function hasHandoutData(
  storage: StorageAdapter = localAdapter,
): boolean {
  return !!storage.getItem(HANDOUT_DATA_KEY);
}

// ─── Demo (v3) last visited step ─────────────────────────────────────

const DEMO_LAST_STEP_KEY = "demo.v3.lastStep";
const DEMO_LAST_SCROLL_KEY = "demo.v3.lastScroll";

export function getDemoLastStep(
  storage: StorageAdapter = localAdapter,
): string | null {
  try {
    return storage.getItem(DEMO_LAST_STEP_KEY);
  } catch {
    return null;
  }
}

export function setDemoLastStep(
  slug: string,
  storage: StorageAdapter = localAdapter,
): void {
  try {
    storage.setItem(DEMO_LAST_STEP_KEY, slug);
  } catch {
    /* ignore */
  }
}

export function getDemoLastScroll(
  storage: StorageAdapter = localAdapter,
): number {
  try {
    const raw = storage.getItem(DEMO_LAST_SCROLL_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function setDemoLastScroll(
  offset: number,
  storage: StorageAdapter = localAdapter,
): void {
  try {
    storage.setItem(DEMO_LAST_SCROLL_KEY, String(Math.max(0, Math.round(offset))));
  } catch {
    /* ignore */
  }
}
